import { FC, ReactNode, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBoolean, useMediaQuery } from 'usehooks-ts';
import { Card } from '@ebsco-ui/ebsco-ui';

import {
  FeatureFlagDisplay,
  ILLLink,
  PlaceHoldModal,
  RemoveHoldModal,
  RenewModal,
  SearchInstanceDto,
} from '@app/components';
import { RecordSummaryContext, SummaryAvailability } from '@app/contexts';
import { useQueryParams } from '@app/hooks';
import { itemStatusPanelButtonMessages } from '@app/translations';
import { breakpoint, Feature, ItemStatusData } from '@app/constants';

import { RecordSummaryCard } from './RecordSummaryCard';
import { ItemStatusPanel } from './ItemStatusPanel';

export type SummaryPlacement =
  | 'results'
  | 'instanceDetails'
  | 'holdsAndCheckouts'
  | 'courses';

export interface RecordSummaryProps {
  instance: SearchInstanceDto;
  placement: SummaryPlacement;
  itemStatusData?: ItemStatusData;
  links?: ReactNode;
  availability?: Partial<SummaryAvailability>;
  ILLLinks?: ILLLink[];
  isAvailabilityLoading?: boolean;
  availabilityLoadingMessage?: string;
  testId?: string;
  altText?: string;
  isRTACShown?: boolean;
  recordNumber?: number;
}

export const RecordSummary: FC<RecordSummaryProps> = props => {
  const {
    placement,
    availability = { isRequestable: true },
    itemStatusData,
    isAvailabilityLoading,
    isRTACShown = true,
  } = props;

  const isMobile = useMediaQuery(
    `(max-width: ${breakpoint.SCREEN.TABLET_SM - 1}px)`
  );
  const { $t } = useIntl();
  const { value: isPlaceHoldModalOpen, toggle: toggleIsPlaceHoldModalOpen } =
    useBoolean(false);
  const { value: isRemoveHoldModalOpen, toggle: toggleIsRemoveHoldModalOpen } =
    useBoolean(false);
  const { value: isRenewHoldModalOpen, toggle: toggleIsRenewHoldModalOpen } =
    useBoolean(false);
  const [removeHoldError, setRemoveHoldError] = useState({
    isError: false,
    errorMessage: '',
  });
  const { isPlaceHold } = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isInstanceDetails = placement === 'instanceDetails';
  const isMobileInstanceDetails = isMobile && isInstanceDetails;
  const isHoldsAndCheckouts = placement === 'holdsAndCheckouts';
  const isMobileHoldsAndCheckouts = isMobile && isHoldsAndCheckouts;
  const isAvailabilityLoadingForTheFirstTime = Boolean(
    isAvailabilityLoading && !availability.status
  );
  const shouldOpenPlaceHoldModal = Boolean(isPlaceHold);
  const isRenew =
    itemStatusData?.actionButtonText ===
    $t(itemStatusPanelButtonMessages.renew);

  const handleModalOpenFromItemStatusPanel = isRenew
    ? toggleIsRenewHoldModalOpen
    : toggleIsRemoveHoldModalOpen;

  const getItemStatusPanel = () => (
    <ItemStatusPanel
      itemStatusData={itemStatusData as ItemStatusData}
      isRemoveHoldError={removeHoldError.isError}
      errorMessage={removeHoldError.errorMessage}
      onModalOpen={handleModalOpenFromItemStatusPanel}
      onErrorClose={() =>
        setRemoveHoldError({ isError: false, errorMessage: '' })
      }
    />
  );

  const handlePlaceHoldModalClose = useCallback(() => {
    toggleIsPlaceHoldModalOpen();

    if (shouldOpenPlaceHoldModal) {
      navigate(location.pathname);
    }
  }, [
    location.pathname,
    navigate,
    shouldOpenPlaceHoldModal,
    toggleIsPlaceHoldModalOpen,
  ]);

  return (
    <RecordSummaryContext.Provider
      value={{
        isInstanceDetails,
        isMobileInstanceDetails,
        isHoldsAndCheckouts,
        isMobileHoldsAndCheckouts,
        isAvailabilityLoadingForTheFirstTime,
        getItemStatusPanel,
        toggleIsPlaceHoldModalOpen,
        ...props,
      }}
    >
      <Card border="bottom">
        <RecordSummaryCard isRTACShown={isRTACShown} />
        <FeatureFlagDisplay
          flagToMatch={{ name: Feature.placeHold, isActiveIfNotFound: true }}
        >
          <PlaceHoldModal
            isOpen={isPlaceHoldModalOpen || shouldOpenPlaceHoldModal}
            onModalClose={handlePlaceHoldModalClose}
          />
        </FeatureFlagDisplay>
        {itemStatusData?.itemId && (
          <RenewModal
            isOpen={isRenewHoldModalOpen}
            onModalClose={toggleIsRenewHoldModalOpen}
          />
        )}
        {itemStatusData?.holdId && (
          <RemoveHoldModal
            isOpen={isRemoveHoldModalOpen}
            recordId={itemStatusData.holdId}
            onModalClose={toggleIsRemoveHoldModalOpen}
            onError={setRemoveHoldError}
          />
        )}
      </Card>
    </RecordSummaryContext.Provider>
  );
};
